<script setup>
import ComboboxField from '@js/components/fields/ComboboxField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSuccess } from '@js/utils/toast';
import { computed, reactive, ref } from 'vue';
import { object } from 'yup';

const { tenant, productAttributes, defaultContextAttributeCodes } = defineProps(
    {
        tenant: {
            type: Object,
            required: true,
        },
        productAttributes: {
            type: Array,
            required: false,
            default: () => [],
        },
        defaultContextAttributeCodes: {
            type: Array,
            required: false,
            default: () => [],
        },
    },
);

const { route } = useRoute();

const isSubmitting = ref(false);

const attributeOptions = computed(() =>
    productAttributes.map((attribute) => ({
        ...attribute,
        label: attribute.label || attribute.code,
    })),
);

const form = reactive({
    contextAttributes: attributeOptions.value.filter((attribute) =>
        defaultContextAttributeCodes.includes(attribute.code),
    ),
});

const validationSchema = object({});

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route(
                'front.projectManagement.ai.postProductContentConfiguration',
                {
                    tenant: tenant.id,
                },
            ),
            {
                contextAttributeCodes: form.contextAttributes.map(
                    (attribute) => attribute.code,
                ),
            },
        )
        .then(() => {
            toastSuccess('Configuration enregistrée.');
        })
        .catch((error) => {
            toastError(error);
        })
        .finally(() => {
            isSubmitting.value = false;
        });
}
</script>

<template>
    <FormRoot
        v-slot="{ errors }"
        :validation-schema="validationSchema"
        :submit="submit"
    >
        <FormColumn>
            <FormContent>
                <ComboboxField
                    v-model="form.contextAttributes"
                    field-name="contextAttributes"
                    field-id="contextAttributes"
                    filtered-option-key="label"
                    :options="attributeOptions"
                    by="code"
                    multiple
                    label="Attributs de contexte par défaut"
                    legend="Sélectionne les attributs produits dont les valeurs stockées seront fournies par défaut à l'IA comme données factuelles lors de la génération. Ce choix pré-remplit le modal de génération et reste ajustable par lot."
                >
                    <template #optionContent="{ option }">
                        {{ option.label }}
                        <span class="text-xs text-gray-400">
                            ({{ option.code }})
                        </span>
                    </template>
                </ComboboxField>
            </FormContent>
        </FormColumn>

        <FormFooter
            :is-submitting="isSubmitting"
            :errors="errors"
            without-back-button
        />
    </FormRoot>
</template>
